import React, { useState } from 'react';
import { X, Copy, CheckCircle, Code, Link } from 'lucide-react';
import Toast from './Toast';

const COPY_LABELS = {
  url: 'URL',
  iframe: 'Embed code',
  callButton: 'Call button',
  buttonEmbed: 'Embed snippet',
  scriptSnippet: 'JavaScript snippet',
};

const EmbedModal = ({ agent, onClose }) => {
  const [copySuccess, setCopySuccess] = useState({
    url: false,
    iframe: false,
    callButton: false,
    buttonEmbed: false,
    scriptSnippet: false,
  });
  const [toast, setToast] = useState(null);
  
  const baseUrl = window.location.origin;
  const embedUrl = `${baseUrl}/embed/agent/${agent.id}`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" allow="microphone"></iframe>`;
  const buttonCode = `<a href="${embedUrl}" target="_blank" style="display: inline-block; padding: 12px 24px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;">Call Agent</a>`;
  const minimalEmbedCode = `<iframe src="${embedUrl}?view=buttons" width="100%" height="100" frameborder="0" allow="microphone" style="border-radius: 16px;"></iframe>`;
  const buttonEmbedUrl = `${embedUrl}?view=buttons`;
  const scriptSnippet = `(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${buttonEmbedUrl}';
  iframe.style.cssText = 'position:fixed;bottom:24px;right:24px;width:120px;height:120px;border:none;border-radius:0;z-index:999999;background:transparent;';
  iframe.setAttribute('allow', 'microphone');
  document.body.appendChild(iframe);
})();`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess({ ...copySuccess, [type]: true });
      setToast({ message: `${COPY_LABELS[type] || 'Code'} copied to clipboard!`, type: 'success' });
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      setToast({ message: 'Failed to copy to clipboard', type: 'error' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        {/* Header */}
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Code className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Embed Agent: {agent.name}</h2>
              <p className="text-white/70 text-xs mt-0.5 font-normal">Get embed code to place anywhere</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Embed URL */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Embed URL</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm text-foreground break-all">
                {embedUrl}
              </div>
              <button
                onClick={() => handleCopy(embedUrl, 'url')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2"
              >
                {copySuccess.url ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Use this URL to link directly to your agent's call interface
            </p>
          </div>

          {/* Full Page Embed */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Full Page Embed (iframe)</h3>
            </div>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={embedCode}
                className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground resize-none"
                rows={3}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => handleCopy(embedCode, 'iframe')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.iframe ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Embed the full call interface in your website using an iframe
            </p>
          </div>

          {/* Button Link Code */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Call Button HTML</h3>
            </div>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={buttonCode}
                className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground resize-none"
                rows={2}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => handleCopy(buttonCode, 'callButton')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.callButton ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Add a simple call button that links to your agent
            </p>
          </div>

          {/* Minimal Start/Stop Embed */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Start/Stop Button Embed</h3>
            </div>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={minimalEmbedCode}
                className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground resize-none"
                rows={3}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => handleCopy(minimalEmbedCode, 'buttonEmbed')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.buttonEmbed ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Inline embed that shows only Start Session / Stop Session buttons—perfect for demos on external sites.
            </p>
          </div>

          {/* JavaScript Snippet */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">JavaScript Snippet</h3>
            </div>
            <div className="flex gap-2">
              <textarea
                readOnly
                value={scriptSnippet}
                className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground resize-none"
                rows={8}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => handleCopy(scriptSnippet, 'scriptSnippet')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.scriptSnippet ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Add this script to your page to inject the floating voice agent button. No iframe needed—works with any site structure.
            </p>
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Preview</h3>
            <div className="p-4 bg-muted/20 border border-border rounded-lg">
              <p className="text-xs text-muted-foreground mb-2">Click the button below to test:</p>
              <a
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors font-semibold"
              >
                Call Agent
              </a>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default EmbedModal;

